import { useState } from "react";
import { Link } from "react-router-dom";
import { Car, Truck, Caravan, Gauge, ShieldCheck, Sparkles, Droplets, TrendingUp, Sun, Home, RotateCcw, ArrowRight, ArrowLeft } from "lucide-react";
import { useSettings } from "@/context/SettingsContext";
import { whatsappLink } from "@/lib/contact";

const STEPS = [
  {
    key: "type",
    q: "Araç tipiniz nedir?",
    options: [
      { v: "binek", label: "Binek / Sedan", icon: Car },
      { v: "suv", label: "SUV / Crossover", icon: Truck },
      { v: "ticari", label: "Ticari / Van", icon: Caravan },
    ],
  },
  {
    key: "age",
    q: "Aracınızın yaşı?",
    options: [
      { v: "yeni", label: "0 - 2 yaş (Sıfır/Yeni)", icon: Sparkles },
      { v: "orta", label: "3 - 7 yaş", icon: Gauge },
      { v: "eski", label: "8 yaş ve üzeri", icon: RotateCcw },
    ],
  },
  {
    key: "usage",
    q: "Kullanım şekliniz?",
    options: [
      { v: "sehir", label: "Şehir içi günlük", icon: Car },
      { v: "uzunyol", label: "Sık uzun yol", icon: Gauge },
      { v: "az", label: "Az kullanım", icon: Home },
    ],
  },
  {
    key: "park",
    q: "Aracınızı nerede park ediyorsunuz?",
    options: [
      { v: "disarida", label: "Açık alanda / Dışarıda", icon: Sun },
      { v: "garaj", label: "Kapalı garajda", icon: Home },
    ],
  },
  {
    key: "priority",
    q: "Önceliğiniz nedir?",
    options: [
      { v: "parlaklik", label: "Parlaklık & Estetik", icon: Sparkles },
      { v: "koruma", label: "Maksimum Koruma", icon: ShieldCheck },
      { v: "temizlik", label: "Temizlik & Hijyen", icon: Droplets },
      { v: "deger", label: "Değer Koruma", icon: TrendingUp },
    ],
  },
];

const SERVICE_MAP = {
  "seramik-kaplama-kocaeli": "Seramik Kaplama",
  "ppf-kaplama-kocaeli": "PPF Kaplama",
  "boya-koruma-kocaeli": "Boya Koruma",
  "detayli-ic-temizlik-kocaeli": "Detaylı İç Temizlik",
  "cam-filmi-kocaeli": "Cam Filmi",
  "hare-giderme-kocaeli": "Hare Giderme",
  "ozon-dezenfeksiyon-kocaeli": "Ozon Dezenfeksiyon",
};

function compute(answers) {
  const rec = new Set();
  let score = 62;

  if (answers.priority === "parlaklik") {
    rec.add("seramik-kaplama-kocaeli");
    rec.add("boya-koruma-kocaeli");
    score += 6;
  } else if (answers.priority === "koruma") {
    rec.add("ppf-kaplama-kocaeli");
    rec.add("seramik-kaplama-kocaeli");
    score += 12;
  } else if (answers.priority === "temizlik") {
    rec.add("detayli-ic-temizlik-kocaeli");
    rec.add("ozon-dezenfeksiyon-kocaeli");
    score += 4;
  } else if (answers.priority === "deger") {
    rec.add("ppf-kaplama-kocaeli");
    rec.add("seramik-kaplama-kocaeli");
    rec.add("cam-filmi-kocaeli");
    score += 10;
  }

  if (answers.park === "disarida") {
    rec.add("cam-filmi-kocaeli");
    rec.add("boya-koruma-kocaeli");
    score -= 8;
  } else if (answers.park === "garaj") {
    score += 8;
  }

  if (answers.age === "eski") {
    rec.add("hare-giderme-kocaeli");
    score -= 6;
  } else if (answers.age === "yeni") {
    rec.add("seramik-kaplama-kocaeli");
    score += 10;
  }

  if (answers.usage === "uzunyol") {
    rec.add("ppf-kaplama-kocaeli");
    score -= 4;
  } else if (answers.usage === "az") {
    score += 4;
  }

  score = Math.max(35, Math.min(98, score));
  return { services: Array.from(rec).slice(0, 4), score };
}

export default function AdvisorQuiz() {
  const settings = useSettings();
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const [done, setDone] = useState(false);

  const current = STEPS[step];
  const progress = Math.round(((done ? STEPS.length : step) / STEPS.length) * 100);

  function pick(v) {
    const next = { ...answers, [current.key]: v };
    setAnswers(next);
    if (step + 1 < STEPS.length) {
      setStep(step + 1);
    } else {
      setDone(true);
    }
  }

  function reset() {
    setAnswers({});
    setStep(0);
    setDone(false);
  }

  const result = done ? compute(answers) : null;

  return (
    <div
      data-testid="advisor-quiz-module"
      className="relative overflow-hidden border border-white/12 bg-gradient-to-b from-[#101010] to-[#070707] p-6 backdrop-blur-2xl sm:p-10"
    >
      <div className="absolute inset-x-0 top-0 h-px metallic-line" />
      <div className="mb-8 h-1 w-full overflow-hidden bg-white/10">
        <div className="h-full bg-gold transition-all duration-500" style={{ width: `${progress}%` }} />
      </div>

      {!done ? (
        <div>
          <span className="font-oswald text-xs font-bold uppercase tracking-[0.25em] text-gold">ADIM {step + 1}/{STEPS.length}</span>
          <h3 className="mt-3 font-display text-2xl font-medium text-white sm:text-3xl">{current.q}</h3>

          <div className="mt-7 grid gap-2.5 sm:grid-cols-2">
            {current.options.map((opt) => {
              const Icon = opt.icon;
              const active = answers[current.key] === opt.v;
              return (
                <button
                  key={opt.v}
                  data-testid={`advisor-option-${current.key}-${opt.v}`}
                  onClick={() => pick(opt.v)}
                  className={`group flex items-center gap-4 border p-5 text-left transition-all duration-300 ${
                    active ? "border-gold bg-gold/10" : "border-white/10 bg-white/[0.02] hover:border-gold/40 hover:bg-white/[0.05]"
                  }`}
                >
                  <span className={`flex h-11 w-11 shrink-0 items-center justify-center border ${active ? "border-gold bg-gold text-black" : "border-white/10 bg-white/5 text-gold"}`}>
                    <Icon className="h-5 w-5" />
                  </span>
                  <span className="font-semibold text-white">{opt.label}</span>
                </button>
              );
            })}
          </div>

          {step > 0 && (
            <button
              onClick={() => setStep(step - 1)}
              data-testid="advisor-back"
              className="mt-6 inline-flex items-center gap-2 text-sm text-neutral-400 transition-colors hover:text-gold"
            >
              <ArrowLeft className="h-4 w-4" /> Geri
            </button>
          )}
        </div>
      ) : (
        <div data-testid="advisor-result" className="animate-fade-in">
          <span className="font-oswald text-xs font-bold uppercase tracking-[0.25em] text-gold">SİZE ÖZEL ÖNERİ</span>
          <div className="mt-4 flex flex-col gap-6 sm:flex-row sm:items-center">
            <div className="flex items-center gap-4">
              <div className="relative flex h-24 w-24 shrink-0 rotate-45 items-center justify-center border-2 border-gold/30">
                <span className="-rotate-45 font-display text-3xl font-semibold text-gold">{result.score}</span>
              </div>
              <div>
                <p className="text-sm text-neutral-400">Araç Değer Koruma Skoru</p>
                <p className="font-display text-xl text-white">
                  {result.score >= 80 ? "Aracınız iyi korunuyor" : result.score >= 60 ? "Geliştirilebilir" : "Koruma öneriliyor"}
                </p>
              </div>
            </div>
          </div>

          <p className="mt-6 text-neutral-300">Cevaplarınıza göre aracınız için önerilen koruma adımları:</p>
          <div className="mt-4 flex flex-wrap gap-2.5">
            {result.services.map((slug) => (
              <Link
                key={slug}
                to={`/hizmetler/${slug}`}
                data-testid={`advisor-rec-${slug}`}
                className="inline-flex items-center gap-2 border border-gold/40 bg-gold/10 px-4 py-2 text-sm font-semibold text-gold transition-colors hover:bg-gold/20"
              >
                {SERVICE_MAP[slug] || slug} <ArrowRight className="h-3.5 w-3.5" />
              </Link>
            ))}
          </div>

          <div className="mt-8 flex flex-col gap-3 sm:flex-row">
            <a
              href={whatsappLink(
                settings,
                `Merhaba, araç koruma danışmanınızı tamamladım. Önerilen hizmetler: ${result.services.map((s) => SERVICE_MAP[s]).join(", ")}. Bilgi almak istiyorum.`
              )}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="advisor-whatsapp"
              className="inline-flex items-center justify-center gap-2 bg-gold px-6 py-3.5 font-oswald text-sm font-bold uppercase tracking-[0.12em] text-black transition-colors hover:bg-gold-300"
            >
              Bu Planı WhatsApp'tan İlet
            </a>
            <button
              onClick={reset}
              data-testid="advisor-reset"
              className="inline-flex items-center justify-center gap-2 border border-white/15 px-6 py-3.5 text-sm font-semibold text-white transition-colors hover:border-gold/40"
            >
              <RotateCcw className="h-4 w-4" /> Tekrar Başlat
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
